import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { itemImage } from "@/lib/api";
import { bumpWorth } from "@/lib/worth";
import { api, money } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PostCard, MarketCard, OfferRow, UserChip } from "@/components/Social";
import { SizeChips } from "@/components/Trade";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Users, Store, Inbox, Sparkles, Search, Ruler, Scale, Check, X, ExternalLink, UserRound } from "lucide-react";
import { toast } from "sonner";

export default function Social() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const tab = params.get("tab") || "feed";
  // old deep links: market/offers now live on /market
  useEffect(() => {
    if (tab === "market" || tab === "offers") {
      const next = new URLSearchParams(params); next.set("tab", tab === "market" ? (params.get("match_for") ? "browse" : "foryou") : "offers");
      navigate(`/market?${next.toString()}`, { replace: true });
    }
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="social-page">
      <p className="eyebrow">Social</p>
      <h1 className="font-display text-[34px] sm:text-[44px] leading-[1.02] mt-1">Your people</h1>
      <p className="text-muted-foreground mt-1.5">Friends, the people you follow and the sellers you like - their looks, their shelves, your feed.</p>
      <Tabs value={tab === "people" ? "people" : "feed"} onValueChange={(v) => setParams({ tab: v })} className="mt-5">
        <TabsList className="rounded-full">
          <TabsTrigger value="feed" className="rounded-full" data-testid="social-tab-feed"><Sparkles className="h-4 w-4 mr-1.5" /> Feed</TabsTrigger>
          <TabsTrigger value="people" className="rounded-full" data-testid="social-tab-people"><Users className="h-4 w-4 mr-1.5" /> People</TabsTrigger>
        </TabsList>
        <TabsContent value="feed" className="mt-5"><Feed /></TabsContent>
        <TabsContent value="people" className="mt-5"><People /></TabsContent>
      </Tabs>
    </div>
  );
}

const Feed = () => {
  const [scope, setScope] = useState(() => localStorage.getItem("tl.feed.scope") || "following");
  useEffect(() => { localStorage.setItem("tl.feed.scope", scope); }, [scope]);
  const [posts, setPosts] = useState(null);
  const load = () => { api.feed(scope).then(setPosts).catch(() => setPosts([])); };
  useEffect(() => { setPosts(null); load(); }, [scope]); // eslint-disable-line react-hooks/exhaustive-deps
  const del = async (p) => { try { await api.deletePost(p.id); setPosts((x) => x.filter((y) => y.id !== p.id)); toast.success("Post removed"); } catch { toast.error("Could not delete"); } };
  return (
    <div>
      <div className="inline-flex rounded-full bg-secondary p-0.5 text-sm mb-4">
        {["all", "following", "mine"].map((s) => <button key={s} onClick={() => setScope(s)} className={`px-3 py-1 rounded-full capitalize ${scope === s ? "bg-foreground text-background" : ""}`} data-testid={`feed-scope-${s}`}>{s === "all" ? "Everyone" : s === "mine" ? "My looks" : "Following"}</button>)}
      </div>
      {posts === null ? <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-96 rounded-[var(--radius)]" />)}</div>
        : posts.length === 0 ? (
          <div className="rounded-[var(--radius)] border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="feed-empty">
            {scope === "mine" ? "You haven't shared a look yet — render an outfit in the Planner and hit Share." : scope === "following" ? "Follow some people to fill this feed." : "No looks yet. Be the first to share one from the Planner."}
          </div>
        ) : <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="feed-list">{posts.map((p) => <PostCard key={p.id} post={p} onDelete={del} onChange={load} />)}</div>}
    </div>
  );
};

/* ---------- my shelf: everything I've listed + the sell pile, with quick-select list / unlist ---------- */
export const MyShelf = () => {
  const { user: me } = useAuth();
  const [items, setItems] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const load = () => api.myListings().then((r) => setItems(r.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const listed = (items || []).filter((i) => ["sell", "swap", "both"].includes(i.listing_type));
  const pile = (items || []).filter((i) => ["sell", "too_small", "dont_like"].includes(i.intent) && !["sell", "swap", "both", "sold"].includes(i.listing_type));
  const sold = (items || []).filter((i) => i.listing_type === "sold");
  const toggle = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const act = async (mode) => {
    if (!picked.size) return;
    setBusy(true);
    try { await api.bulkList([...picked], mode, "worth"); toast.success(mode === "none" ? `${picked.size} unlisted` : `${picked.size} listed`); setPicked(new Set()); load(); bumpWorth(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update"); }
    finally { setBusy(false); }
  };
  const Tile = ({ it, dim }) => {
    const on = picked.has(it.id);
    return (
      <button type="button" onClick={() => toggle(it.id)} className={`text-left rounded-2xl border p-2 transition-colors ${on ? "border-foreground" : "border-border hover:border-foreground/40"} ${dim ? "opacity-80" : ""}`} data-testid={`myshelf-item-${it.id}`}>
        <div className="relative aspect-[4/5] rounded-xl overflow-hidden bg-white border border-border">
          <img src={itemImage(it)} alt={it.name} className="h-full w-full object-contain" loading="lazy" />
          <span className={`absolute top-2 left-2 h-6 w-6 rounded-full grid place-items-center border-2 ${on ? "bg-foreground border-foreground text-background" : "bg-background/90 border-border text-transparent"}`}><Check className="h-3.5 w-3.5" /></span>
          {it.listing_type && it.listing_type !== "none" && <span className="absolute bottom-2 left-2 rounded-full bg-foreground text-background px-2 h-5 inline-flex items-center text-[10px] font-semibold">{it.listing_type === "sold" ? "Sold" : it.listing_type === "swap" ? "Swap" : it.listing_type === "both" ? "Sell · Swap" : "For sale"}</span>}
          {it.external_listings?.some((e) => e.provider === "ebay") && <span className="absolute bottom-2 right-2 rounded-full bg-background/90 px-2 h-5 inline-flex items-center text-[10px] font-medium">eBay</span>}
        </div>
        <p className="text-xs font-medium mt-1.5 truncate">{it.listing_title || it.name}</p>
        <p className="text-[11px] text-muted-foreground">{it.listing_price != null ? money(it.listing_price) : it.worth != null ? `worth ${money(it.worth)}` : ""}{it.matches ? ` · ${it.matches} trade match${it.matches === 1 ? "" : "es"}` : ""}</p>
      </button>
    );
  };
  if (items === null) return <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="aspect-[3/4] rounded-2xl" />)}</div>;
  return (
    <div data-testid="my-shelf">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-muted-foreground">{listed.length} live · {pile.length} in the sell pile · {sold.length} sold</p>
        <div className="flex items-center gap-2">
          {me?.handle && <Button asChild variant="outline" size="sm" className="rounded-full"><Link to={`/u/${me.handle}`} data-testid="myshelf-open-store"><UserRound className="h-4 w-4 mr-1.5" /> My store page <ExternalLink className="h-3 w-3 ml-1.5" /></Link></Button>}
          <Button asChild variant="ghost" size="sm" className="rounded-full"><Link to="/review" data-testid="myshelf-open-review">Final pass</Link></Button>
        </div>
      </div>
      {listed.length === 0 && pile.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="myshelf-empty">Nothing on your shelf yet. Rate pieces in the <Link to="/review" className="underline">Final pass</Link> or hit Select in your closet and list what you don't wear.</div>
      ) : (
        <>
          {listed.length > 0 && <><p className="eyebrow mb-2">Live on ThreadList</p><div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5" data-testid="myshelf-listed">{listed.map((it) => <Tile key={it.id} it={it} />)}</div></>}
          {pile.length > 0 && <><p className="eyebrow mt-6 mb-2">Sell pile - not listed yet</p><div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5" data-testid="myshelf-pile">{pile.map((it) => <Tile key={it.id} it={it} dim />)}</div></>}
          {sold.length > 0 && <><p className="eyebrow mt-6 mb-2">Sold</p><div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 opacity-70" data-testid="myshelf-sold">{sold.map((it) => <Tile key={it.id} it={it} dim />)}</div></>}
        </>
      )}
      {picked.size > 0 && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-20 md:bottom-6 z-40 rounded-full bg-foreground text-background shadow-lift px-3 py-2 flex items-center gap-1.5 flex-wrap justify-center max-w-[95vw]" data-testid="myshelf-bulk-bar">
          <span className="text-xs px-2">{picked.size} selected</span>
          <Button size="sm" disabled={busy} onClick={() => act("sell")} className="rounded-full h-8 bg-background text-foreground hover:bg-background/90" data-testid="myshelf-bulk-sell">List for sale</Button>
          <Button size="sm" disabled={busy} onClick={() => act("swap")} className="rounded-full h-8 bg-background text-foreground hover:bg-background/90" data-testid="myshelf-bulk-swap">Swap</Button>
          <Button size="sm" disabled={busy} onClick={() => act("both")} variant="ghost" className="rounded-full h-8 text-background hover:bg-background/10" data-testid="myshelf-bulk-both">Both</Button>
          <Button size="sm" disabled={busy} onClick={() => act("none")} variant="ghost" className="rounded-full h-8 text-background hover:bg-background/10" data-testid="myshelf-bulk-unlist">Unlist</Button>
          <Button size="sm" variant="ghost" onClick={() => setPicked(new Set())} className="rounded-full h-8 text-background hover:bg-background/10" aria-label="Clear"><X className="h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
};

export const BrowseMarket = () => {
  const [params] = useSearchParams();
  const [kind, setKind] = useState(params.get("view") === "mine" ? "mine" : "all");
  const [fit, setFit] = useState(() => localStorage.getItem("attire.market.fit") !== "off");
  const [matchFor, setMatchFor] = useState(params.get("match_for") || "none");
  const [mine, setMine] = useState([]);
  const [data, setData] = useState(null);
  const load = () => {
    const params = { fit };
    if (kind === "mine") return;
    if (kind !== "all") params.kind = kind;
    if (matchFor !== "none") params.match_for = matchFor;
    api.market(params).then(setData).catch((e) => { toast.error(e?.response?.data?.detail || "Could not load market"); setData({ items: [], sizes: null }); });
  };
  useEffect(() => { setData(null); load(); }, [kind, fit, matchFor]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.myListings().then((r) => setMine(r.items.filter((i) => ["swap", "both"].includes(i.listing_type)))).catch(() => {}); }, []);
  useEffect(() => { localStorage.setItem("attire.market.fit", fit ? "on" : "off"); }, [fit]);
  const items = data?.items;
  const anchor = data?.anchor;
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-full bg-secondary p-0.5 text-sm">
            {[["all", "Everything"], ["sell", "For sale"], ["swap", "Swaps"]].map(([k, l]) => <button key={k} onClick={() => setKind(k)} disabled={matchFor !== "none"} className={`px-3 py-1 rounded-full disabled:opacity-50 ${kind === k ? "bg-foreground text-background" : ""}`} data-testid={`market-kind-${k}`}>{l}</button>)}
          </div>
          <label className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-sm"><Ruler className="h-4 w-4" /> Fits me <Switch checked={fit} onCheckedChange={setFit} data-testid="market-fit-switch" /></label>
          {mine.length > 0 && (
            <Select value={matchFor} onValueChange={setMatchFor}>
              <SelectTrigger className="h-9 w-[220px] rounded-full" data-testid="market-match-for-select"><Scale className="h-4 w-4 mr-1.5" /><SelectValue placeholder="Trade matches for…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">All listings</SelectItem>
                {mine.map((m) => <SelectItem key={m.id} value={m.id}>Trades for: {m.name}{m.worth != null ? ` (${money(m.worth)})` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
        <SizeChips sizes={data?.sizes} />
      </div>
      {kind === "mine" ? <MyShelf /> : <>
      {anchor && data?.window && (
        <div className="mb-4 rounded-xl bg-good/20 px-3 py-2 text-sm flex items-center gap-2 flex-wrap" data-testid="market-match-banner">
          <Scale className="h-4 w-4" /> Showing swaps worth <b className="font-mono-plex">{money(data.window[0])}–{money(data.window[1])}</b> — a fair trade for your <b>{anchor.name}</b> ({money(anchor.worth)}). <button className="underline text-xs" onClick={() => setMatchFor("none")} data-testid="market-match-clear">Clear</button>
        </div>
      )}
      {items == null ? <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="aspect-[3/4] rounded-[var(--radius)]" />)}</div>
        : items.length === 0 ? <div className="rounded-[var(--radius)] border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="market-empty">{fit ? <>Nothing in your size right now. <button className="underline" onClick={() => setFit(false)} data-testid="market-fit-off-link">Show all sizes</button></> : anchor ? "No fair swaps for that piece yet — check back soon." : "Nothing listed yet. List a piece from your Profile → Manage listings."}</div>
        : <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5" data-testid="market-list">{items.map((it) => <MarketCard key={it.id} item={it} onOffered={load} anchorWorth={anchor?.worth} />)}</div>}
      </>}
    </div>
  );
};

const PersonCard = ({ p, onToggle, extra }) => (
  <div className="rounded-[var(--radius)] border border-border bg-card p-3 flex items-center justify-between gap-3" data-testid={`person-${p.handle}`}>
    <div className="min-w-0">
      <UserChip user={p} />
      <p className="text-[11px] text-muted-foreground mt-1.5">{p.followers} followers · {p.posts} looks · {p.live_listings ?? p.listed} listed</p>
      {extra}
    </div>
    <Button size="sm" variant={p.is_following ? "outline" : "default"} onClick={() => onToggle(p)} className="rounded-full shrink-0" data-testid={`follow-${p.handle}`}>{p.is_following ? "Following" : "Follow"}</Button>
  </div>
);

const People = () => {
  const { user: me } = useAuth();
  const [q, setQ] = useState("");
  const [people, setPeople] = useState(null);
  const [following, setFollowing] = useState(null);
  const [suggested, setSuggested] = useState(null);
  const loadSides = () => { api.peopleFollowing().then(setFollowing).catch(() => setFollowing([])); api.peopleSuggested().then(setSuggested).catch(() => setSuggested([])); };
  useEffect(() => { loadSides(); }, []);
  useEffect(() => { if (!q) { setPeople(null); return undefined; } const t = setTimeout(() => api.people(q).then(setPeople).catch(() => setPeople([])), 250); return () => clearTimeout(t); }, [q]);
  const toggle = async (p) => {
    try {
      if (p.is_following) await api.unfollow(p.handle); else await api.follow(p.handle);
      const upd = (list) => list && list.map((x) => (x.id === p.id ? { ...x, is_following: !p.is_following, followers: x.followers + (p.is_following ? -1 : 1) } : x));
      setPeople(upd); setSuggested(upd); setFollowing(upd);
      toast.success(p.is_following ? `Unfollowed @${p.handle}` : `Following @${p.handle} - their listings now rank higher in your Market`);
      setTimeout(loadSides, 400);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };
  return (
    <div>
      <div className="relative max-w-sm mb-5">
        <Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people…" className="pl-9" data-testid="people-search-input" />
      </div>
      {!q && (
        <div className="space-y-7">
          <section data-testid="people-following">
            <p className="eyebrow mb-2">Following{following ? ` · ${following.length}` : ""}</p>
            {following === null ? <Skeleton className="h-24 rounded-[var(--radius)]" />
              : following.length === 0 ? <div className="rounded-[var(--radius)] border border-dashed border-border p-6 text-sm text-muted-foreground" data-testid="people-following-empty">You're not following anyone yet. Pick a few sellers below - your feed and Market curate around them.</div>
              : <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{following.map((p) => <PersonCard key={p.id} p={p} onToggle={toggle} />)}</div>}
          </section>
          <section data-testid="people-suggested">
            <p className="eyebrow mb-2">Sellers you'd like</p>
            <p className="text-xs text-muted-foreground mb-3">Ranked by your brands, your sizes, polished shelves and looks you've liked.</p>
            {suggested === null ? <Skeleton className="h-24 rounded-[var(--radius)]" />
              : suggested.length === 0 ? <div className="rounded-[var(--radius)] border border-dashed border-border p-6 text-sm text-muted-foreground" data-testid="people-suggested-empty">Nobody else here yet - invite friends to sign up and follow you @{me?.handle}.</div>
              : <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{suggested.map((p) => <PersonCard key={p.id} p={p} onToggle={toggle} extra={p.why?.length ? <p className="text-[11px] mt-1 inline-flex flex-wrap gap-1">{p.why.map((w) => <span key={w} className="rounded-full bg-secondary px-2 h-5 inline-flex items-center">{w}</span>)}</p> : null} />)}</div>}
          </section>
        </div>
      )}
      {q && (people === null ? <Skeleton className="h-40 rounded-[var(--radius)]" />
        : people.length === 0 ? <div className="rounded-[var(--radius)] border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="people-empty">{q ? "No one matches that." : `Nobody else here yet — invite friends to sign up and follow you @${me?.handle}.`}</div>
        : <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="people-list">{people.map((p) => <PersonCard key={p.id} p={p} onToggle={toggle} />)}</div>)}
    </div>
  );
};

export const Offers = ({ onChanged }) => {
  const [params] = useSearchParams();
  const openId = params.get("open") || null;
  const [box, setBox] = useState(params.get("box") === "sent" ? "sent" : "inbox");
  const [offers, setOffers] = useState(null);
  const load = () => { api.offers(box).then(setOffers).catch(() => setOffers([])); onChanged?.(); };
  useEffect(() => { setOffers(null); load(); }, [box]); // eslint-disable-line react-hooks/exhaustive-deps
  const unreadHere = (offers || []).filter((o) => o.unread).length;
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="inline-flex rounded-full bg-secondary p-0.5 text-sm">
          {[["inbox", "Received"], ["sent", "Sent"]].map(([k, l]) => <button key={k} onClick={() => setBox(k)} className={`px-3 py-1 rounded-full ${box === k ? "bg-foreground text-background" : ""}`} data-testid={`offers-box-${k}`}>{l}</button>)}
        </div>
        <p className="text-xs text-muted-foreground" data-testid="offers-hint">{unreadHere > 0 ? `${unreadHere} conversation${unreadHere === 1 ? "" : "s"} with new messages` : "Open a thread to chat, counter a price, or arrange the hand-off."}</p>
      </div>
      {offers === null ? <Skeleton className="h-40 rounded-[var(--radius)]" />
        : offers.length === 0 ? <div className="rounded-[var(--radius)] border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="offers-empty">{box === "inbox" ? "No offers on your pieces yet." : "You haven't made any offers."}</div>
        : <div className="grid gap-3 lg:grid-cols-2" data-testid="offers-list">{offers.map((o) => <OfferRow key={o.id} offer={o} onChanged={load} defaultOpen={o.id === openId} />)}</div>}
    </div>
  );
};
